/**
 * PassageAnnotationToolbar — the ten marking tools, plus Undo and Clear.
 *
 * Picking a tool switches the passage into draw mode. Tapping the armed tool again puts it
 * down, exactly as the highlighter pens do.
 *
 * Shape is never the sole indicator of which tool is armed (WCAG 1.4.1): the armed tool also
 * takes a ring and a filled background, and carries aria-pressed.
 *
 * Each tool carries its NAME as the visible label and its PURPOSE as the title and accessible
 * name — "Circle" alone means nothing to a ten-year-old, but "circle the characters" is the
 * thing their teacher actually said. The purposes come from the school annotation symbol sheet
 * these tools are modelled on.
 */

import {
  Brackets,
  Circle,
  CircleAlert,
  CircleHelp,
  MoveHorizontal,
  Parentheses,
  PencilLine,
  RefreshCw,
  Square,
  Underline,
  Undo2,
  Waves,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ANNOTATION_SHAPE_ORDER, type AnnotationShape } from '@/lib/passageAnnotations';

const SHAPE_LABELS: Record<AnnotationShape, string> = {
  circle: 'Circle',
  box: 'Box',
  roundBracket: 'Problem',
  squareBracket: 'Solution',
  underline: 'Underline',
  squiggly: 'Unsure',
  link: 'Link',
  question: 'Ask',
  remind: 'Reminds',
  surprise: 'Wow',
};

/** What each mark is for, in the words a teacher would use. */
const SHAPE_PURPOSES: Record<AnnotationShape, string> = {
  circle: 'Circle the characters',
  box: 'Box the setting and the time',
  roundBracket: 'Mark the problem',
  squareBracket: 'Mark the solution',
  underline: 'Underline something important',
  squiggly: 'Mark a word you do not understand',
  link: 'Link a pronoun to what it refers to',
  question: 'Something you want to ask about',
  remind: 'What this reminds you of',
  surprise: 'Surprising information',
};

const SHAPE_ICONS: Record<AnnotationShape, typeof Circle> = {
  circle: Circle,
  box: Square,
  roundBracket: Parentheses,
  squareBracket: Brackets,
  underline: Underline,
  squiggly: Waves,
  link: MoveHorizontal,
  question: CircleHelp,
  remind: RefreshCw,
  surprise: CircleAlert,
};

export interface PassageAnnotationToolbarProps {
  armedShape: AnnotationShape | null;
  onArm: (shape: AnnotationShape | null) => void;
  onUndo: () => void;
  onClear: () => void;
  /** Disables Undo and Clear when there is nothing to undo or clear. */
  hasMarks: boolean;
  /**
   * Rendered at the end of the row. Used for the highlighter disclosure, which shares this row
   * rather than taking one of its own — vertical space is scarce on the narrow PassageCard,
   * where the passage itself is capped at max-h-48.
   */
  trailing?: React.ReactNode;
}

export function PassageAnnotationToolbar({
  armedShape,
  onArm,
  onUndo,
  onClear,
  hasMarks,
  trailing,
}: PassageAnnotationToolbarProps) {
  return (
    <div
      className="mb-2 flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Passage marking tools"
    >
      {/* Leading affordance so the row reads as "marking tools", not a scatter of glyphs. */}
      <span
        className="flex items-center gap-1 pr-0.5 font-body text-sm font-medium text-otter-earth-700"
        aria-hidden="true"
      >
        <PencilLine className="h-4 w-4" />
        <span className="hidden sm:inline">Mark</span>
      </span>

      {ANNOTATION_SHAPE_ORDER.map((shape) => {
        const isArmed = armedShape === shape;
        const Icon = SHAPE_ICONS[shape];
        return (
          <button
            key={shape}
            type="button"
            aria-label={`${SHAPE_LABELS[shape]} tool: ${SHAPE_PURPOSES[shape].toLowerCase()}`}
            title={SHAPE_PURPOSES[shape]}
            aria-pressed={isArmed}
            // Toggle: tapping the armed tool puts it down and restores normal scroll/select.
            onClick={() => onArm(isArmed ? null : shape)}
            className={cn(
              'flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl px-2 transition-all',
              isArmed
                ? 'bg-otter-earth-800/10 ring-2 ring-otter-earth-700 ring-offset-1'
                : 'hover:bg-otter-sand'
            )}
          >
            <Icon aria-hidden="true" className="h-[18px] w-[18px] text-otter-earth-800" />
            <span className="font-body text-[10px] font-semibold leading-none text-otter-earth-700">
              {SHAPE_LABELS[shape]}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        onClick={onUndo}
        disabled={!hasMarks}
        aria-label="Undo last mark"
        className={cn(
          'ml-auto flex min-h-11 items-center gap-1 rounded-xl px-2.5 font-body text-sm text-otter-earth-700 transition-colors',
          hasMarks ? 'hover:bg-otter-sand' : 'cursor-not-allowed opacity-40'
        )}
      >
        <Undo2 aria-hidden="true" className="h-4 w-4" />
        <span className="hidden sm:inline">Undo</span>
      </button>

      <button
        type="button"
        onClick={onClear}
        disabled={!hasMarks}
        className={cn(
          'min-h-11 rounded-xl px-3 font-body text-sm text-otter-earth-700 transition-colors',
          hasMarks ? 'hover:bg-otter-sand' : 'cursor-not-allowed opacity-40'
        )}
      >
        Clear
      </button>

      {trailing}
    </div>
  );
}
